import type {
  ApartmentFilterParams,
  IndexCategory,
  ListingType
} from '@services/livability/types'

export interface ApartmentFilters {
  minPrice?: number
  maxPrice?: number
  minPricePerM2?: number
  maxPricePerM2?: number
  minArea?: number
  maxArea?: number
  roomCounts?: number[]
  minRooms?: number
  maxRooms?: number
  city?: string
  district?: string
  nearCenter?: boolean
  condition?: string
  buildingType?: string
  hasParking?: boolean
  hasBalcony?: boolean
  hasElevator?: boolean
  petsAllowed?: boolean
  furnished?: boolean
  fullyFurnished?: boolean
  availableNow?: boolean
  sortBy?: string
  rankBy?: 'value' | 'livability'
  indexCategory?: IndexCategory
  minIndex?: number
}

export interface ApartmentViewState {
  filters: ApartmentFilters
  q: string
  page: number
  limit: number
  listingType: ListingType
}

export const DEFAULT_PAGE = 1
export const DEFAULT_LIMIT = 24

const VALID_LISTING_TYPES = new Set<ListingType>(['rent', 'sale'])
const VALID_SORT = new Set([
  'price_asc',
  'price_desc',
  'area_asc',
  'area_desc',
  'newest'
])
const VALID_RANK_BY = new Set(['value', 'livability'])
const VALID_INDEX_CATEGORIES = new Set<IndexCategory>([
  'general',
  'budget',
  'family',
  'student'
])

const MAP_ONLY_KEYS = ['ids', 'from', 'lat', 'lng', 'zoom'] as const

function parseNumber(value: string | null): number | undefined {
  if (!value) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function parsePositiveInt(value: string | null): number | undefined {
  const parsed = parseNumber(value)
  if (parsed === undefined || parsed < 1 || !Number.isInteger(parsed)) {
    return undefined
  }
  return parsed
}

function parseRoomCounts(params: URLSearchParams): number[] | undefined {
  const raw = params.get('room_counts')
  if (raw) {
    const nums = raw
      .split(',')
      .map((part) => parsePositiveInt(part.trim()))
      .filter((n): n is number => n !== undefined)
    const unique = [...new Set(nums)].sort((a, b) => a - b)
    return unique.length > 0 ? unique : undefined
  }
  const legacy = parsePositiveInt(params.get('room_count'))
  return legacy !== undefined ? [legacy] : undefined
}

function formatRoomCountsForUrl(counts: number[]): string {
  return [...new Set(counts)].sort((a, b) => a - b).join(',')
}

function parseBool(value: string | null): true | undefined {
  if (value === '1' || value === 'true') return true
  return undefined
}

function parseListingType(value: string | null): ListingType | undefined {
  if (value && VALID_LISTING_TYPES.has(value as ListingType)) {
    return value as ListingType
  }
  return undefined
}

export function parseApartmentViewParams(
  params: URLSearchParams
): ApartmentViewState {
  const filters: ApartmentFilters = {}

  const minPrice = parseNumber(params.get('min_price'))
  if (minPrice !== undefined) filters.minPrice = minPrice

  const maxPrice = parseNumber(params.get('max_price'))
  if (maxPrice !== undefined) filters.maxPrice = maxPrice

  const minPricePerM2 = parseNumber(params.get('min_price_per_m2'))
  if (minPricePerM2 !== undefined) filters.minPricePerM2 = minPricePerM2

  const maxPricePerM2 = parseNumber(params.get('max_price_per_m2'))
  if (maxPricePerM2 !== undefined) filters.maxPricePerM2 = maxPricePerM2

  const minArea = parseNumber(params.get('min_area'))
  if (minArea !== undefined) filters.minArea = minArea

  const maxArea = parseNumber(params.get('max_area'))
  if (maxArea !== undefined) filters.maxArea = maxArea

  const roomCounts = parseRoomCounts(params)
  if (roomCounts) filters.roomCounts = roomCounts

  const minRooms = parsePositiveInt(params.get('min_rooms'))
  if (minRooms !== undefined) filters.minRooms = minRooms

  const maxRooms = parsePositiveInt(params.get('max_rooms'))
  if (maxRooms !== undefined) filters.maxRooms = maxRooms

  const city = params.get('city')?.trim()
  if (city) filters.city = city

  const district = params.get('district')?.trim()
  if (district) filters.district = district

  const nearCenter = parseBool(params.get('near_center'))
  if (nearCenter) filters.nearCenter = true

  const condition = params.get('condition')?.trim()
  if (condition) filters.condition = condition

  const buildingType = params.get('building_type')?.trim()
  if (buildingType) filters.buildingType = buildingType

  const hasParking = parseBool(params.get('has_parking'))
  if (hasParking) filters.hasParking = true

  const hasBalcony = parseBool(params.get('has_balcony'))
  if (hasBalcony) filters.hasBalcony = true

  const hasElevator = parseBool(params.get('has_elevator'))
  if (hasElevator) filters.hasElevator = true

  const petsAllowed = parseBool(params.get('pets_allowed'))
  if (petsAllowed) filters.petsAllowed = true

  const furnished = parseBool(params.get('furnished'))
  if (furnished) filters.furnished = true

  const fullyFurnished = parseBool(params.get('fully_furnished'))
  if (fullyFurnished) filters.fullyFurnished = true

  const availableNow = parseBool(params.get('available_now'))
  if (availableNow) filters.availableNow = true

  const sortBy = params.get('sort_by')
  if (sortBy && VALID_SORT.has(sortBy)) filters.sortBy = sortBy

  const rankBy = params.get('rank_by')
  if (rankBy && VALID_RANK_BY.has(rankBy)) {
    filters.rankBy = rankBy as 'value' | 'livability'
  }

  const indexCategory = params.get('index_category')
  if (indexCategory && VALID_INDEX_CATEGORIES.has(indexCategory as IndexCategory)) {
    filters.indexCategory = indexCategory as IndexCategory
  }

  const minIndex = parseNumber(params.get('min_index'))
  if (minIndex !== undefined && minIndex >= 0 && minIndex <= 100) {
    filters.minIndex = Math.round(minIndex)
  }

  const listingType = parseListingType(params.get('listing_type')) ?? 'rent'

  return {
    filters,
    q: params.get('q')?.trim() ?? '',
    page: parsePositiveInt(params.get('page')) ?? DEFAULT_PAGE,
    limit: parsePositiveInt(params.get('limit')) ?? DEFAULT_LIMIT,
    listingType
  }
}

function appendBool(params: URLSearchParams, key: string, value?: boolean) {
  if (value) params.set(key, '1')
}

export function buildApartmentFilterSearchParams(
  filters: ApartmentFilters,
  options: {
    q?: string
    page?: number
    limit?: number
    listingType?: ListingType
    includeListParams?: boolean
  } = {}
): URLSearchParams {
  const params = new URLSearchParams()
  const includeListParams = options.includeListParams ?? true

  if (filters.minPrice !== undefined) {
    params.set('min_price', String(filters.minPrice))
  }
  if (filters.maxPrice !== undefined) {
    params.set('max_price', String(filters.maxPrice))
  }
  if (filters.minPricePerM2 !== undefined) {
    params.set('min_price_per_m2', String(filters.minPricePerM2))
  }
  if (filters.maxPricePerM2 !== undefined) {
    params.set('max_price_per_m2', String(filters.maxPricePerM2))
  }
  if (filters.minArea !== undefined) {
    params.set('min_area', String(filters.minArea))
  }
  if (filters.maxArea !== undefined) {
    params.set('max_area', String(filters.maxArea))
  }
  if (filters.roomCounts && filters.roomCounts.length > 0) {
    params.set('room_counts', formatRoomCountsForUrl(filters.roomCounts))
  }
  if (filters.minRooms !== undefined) {
    params.set('min_rooms', String(filters.minRooms))
  }
  if (filters.maxRooms !== undefined) {
    params.set('max_rooms', String(filters.maxRooms))
  }
  if (filters.city) params.set('city', filters.city)
  if (filters.district) params.set('district', filters.district)
  appendBool(params, 'near_center', filters.nearCenter)
  if (filters.condition) params.set('condition', filters.condition)
  if (filters.buildingType) params.set('building_type', filters.buildingType)
  appendBool(params, 'has_parking', filters.hasParking)
  appendBool(params, 'has_balcony', filters.hasBalcony)
  appendBool(params, 'has_elevator', filters.hasElevator)
  appendBool(params, 'pets_allowed', filters.petsAllowed)
  appendBool(params, 'furnished', filters.furnished)
  appendBool(params, 'fully_furnished', filters.fullyFurnished)
  appendBool(params, 'available_now', filters.availableNow)
  if (filters.sortBy) params.set('sort_by', filters.sortBy)
  if (filters.rankBy) params.set('rank_by', filters.rankBy)
  if (filters.indexCategory) {
    params.set('index_category', filters.indexCategory)
  }
  if (filters.minIndex !== undefined) {
    params.set('min_index', String(filters.minIndex))
  }

  if (includeListParams) {
    const q = options.q?.trim() ?? ''
    const page = options.page ?? DEFAULT_PAGE
    const limit = options.limit ?? DEFAULT_LIMIT
    if (q) params.set('q', q)
    if (page > DEFAULT_PAGE) params.set('page', String(page))
    if (limit !== DEFAULT_LIMIT) params.set('limit', String(limit))
  }

  const hasFilters = params.toString() !== ''
  if (hasFilters || options.listingType) {
    params.set('listing_type', options.listingType ?? 'rent')
  }

  return params
}

export function mergeUrlForView(
  target: 'list' | 'map',
  current: URLSearchParams
): URLSearchParams {
  const state = parseApartmentViewParams(current)
  const params = buildApartmentFilterSearchParams(state.filters, {
    listingType: state.listingType,
    includeListParams: target === 'list',
    ...(target === 'list'
      ? { q: state.q, page: state.page, limit: state.limit }
      : {})
  })

  for (const key of MAP_ONLY_KEYS) {
    const value = current.get(key)
    if (value) params.set(key, value)
  }

  if (target === 'map' && !params.get('index_category')) {
    params.set('index_category', state.filters.indexCategory ?? 'general')
  }

  return params
}

export function filtersToApiParams(
  filters: ApartmentFilters,
  options: {
    q?: string
    page?: number
    limit?: number
  } = {}
): ApartmentFilterParams {
  const params: ApartmentFilterParams = {}

  if (filters.minPrice !== undefined) params.minPrice = filters.minPrice
  if (filters.maxPrice !== undefined) params.maxPrice = filters.maxPrice
  if (filters.minPricePerM2 !== undefined) {
    params.minPricePerM2 = filters.minPricePerM2
  }
  if (filters.maxPricePerM2 !== undefined) {
    params.maxPricePerM2 = filters.maxPricePerM2
  }
  if (filters.minArea !== undefined) params.minArea = filters.minArea
  if (filters.maxArea !== undefined) params.maxArea = filters.maxArea
  if (filters.roomCounts && filters.roomCounts.length > 0) {
    params.roomCounts = filters.roomCounts
  }
  if (filters.minRooms !== undefined) params.minRooms = filters.minRooms
  if (filters.maxRooms !== undefined) params.maxRooms = filters.maxRooms
  if (filters.city) params.city = filters.city
  if (filters.district) params.district = filters.district
  if (filters.nearCenter) params.nearCenter = true
  if (filters.condition) params.condition = filters.condition
  if (filters.buildingType) params.buildingType = filters.buildingType
  if (filters.hasParking) params.hasParking = true
  if (filters.hasBalcony) params.hasBalcony = true
  if (filters.hasElevator) params.hasElevator = true
  if (filters.petsAllowed) params.petsAllowed = true
  if (filters.furnished) params.furnished = true
  if (filters.fullyFurnished) params.fullyFurnished = true
  if (filters.availableNow) params.availableNow = true
  if (filters.sortBy) params.sortBy = filters.sortBy
  if (filters.rankBy) params.rankBy = filters.rankBy
  if (filters.indexCategory) params.indexCategory = filters.indexCategory
  if (filters.minIndex !== undefined) params.minIndex = filters.minIndex

  if (options.q?.trim()) params.q = options.q.trim()
  if (options.page !== undefined) params.page = options.page
  if (options.limit !== undefined) params.limit = options.limit

  return params
}

export type FiltersAppliedSource = 'list' | 'map'

export function buildFiltersAppliedEventProps(
  source: FiltersAppliedSource,
  listingType: ListingType,
  filters: ApartmentFilters,
  options: {
    q?: string
    page?: number
    limit?: number
    cleared?: boolean
  } = {}
): Record<string, unknown> {
  if (options.cleared) {
    return { source, listing_type: listingType, cleared: true }
  }
  return {
    source,
    listing_type: listingType,
    ...filtersToApiParams(filters, {
      q: options.q,
      page: options.page,
      limit: options.limit
    })
  }
}

/**
 * `indexCategory` is a view preference (which livability score to display),
 * not a narrowing filter — excluded so map's "index category" buttons don't
 * make "active filters" / "clear filters" appear on a fresh, unfiltered view.
 */
const NON_FILTER_KEYS = new Set<keyof ApartmentFilters>(['indexCategory'])

export function hasActiveApartmentFilters(filters: ApartmentFilters): boolean {
  return (Object.keys(filters) as (keyof ApartmentFilters)[]).some((key) => {
    if (NON_FILTER_KEYS.has(key)) return false
    const value = filters[key]
    if (Array.isArray(value)) return value.length > 0
    return value !== undefined && value !== ''
  })
}
